import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useStyles, VoicemailDetailsDialogProps} from '../utils/VoicemailDetailsDialog.utils';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {PlayingFile} from '../../../store/reducers/calls/reducer';
import {actions} from '../../../store';
import DialogContainer, {DialogButton} from '../../../components/AlertDialog/DialogContainer';
import CallerInfo from '../../../components/Calls/CallerInfo';
import {ReactComponent as DirectionRight} from '../../../assets/direction-right.svg';
import {ReduxState} from '../../../store/types/store';
import {Grid} from "@material-ui/core";
import CustomizedTextField from '../../../components/TextField/TextField';
import {convertUtcToUserLocalTime} from '../../../utils/dateWithTimezoneConversion';
import dayjs from '../../../services/customDayJs';
import classNames from 'classnames';
import {CloudDownload, Delete, StarRate} from '@material-ui/icons';
import InternalAudioPlayer from '../../../components/AudioPlayer/InternalAudioPlayer';
import CustomizedIconButton from '../../../components/IconButton/IconButton';
import Loader from '../../../components/Loader/Loader';
import CopyContentIcon from '../../../components/CopyContentIcon/CopyContentIcon';
import {usePermissionContext} from '../../../hooks/usePermissions';
import {PermissionType} from '../../../store/types/Permission';
import FaxPreview, {FaxPreviewProps} from '../../FaxMailboxes/Details/FaxPreview';
import {VoicemailDetails} from '../../../store/actions/payloads';


const VoicemailDetailsDialog: React.VFC<VoicemailDetailsDialogProps> = (
    {
        isOpen,
        isFax,
        toggleVisibility,
        voicemail,
        timezoneOffset,
        onDeleteClick,
        onDownloadClick,
        extensionId,
        extensionName,
        i_account,
        onClickStarred,
        customDownloadFileAction
    }) => {

    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const permission = usePermissionContext();
    const isReadOnly = permission !== PermissionType.Visible;
    
    const [faxPreview, setFaxPreview] = useState<FaxPreviewProps>({
        url: '',
        fileName: '',
    });

    const {isLoadingVoicemailDetails, voicemailDetails} = useSelector((state: ReduxState) => state.voicemails);
    const {extensionList} = useSelector((state: ReduxState) => state.extensions);
    const {transcription} = useSelector((state: ReduxState) => state.calls);

    const userDateTimeFormat = useSelector<ReduxState, string>(
        (state) =>
            state.accountControlPanel?.accountDetails?.out_date_time_format
            || state.generic.globalCustomerInfo?.customer_info?.out_date_time_format
            || state.generic.sessionData?.out_date_time_format
            || '',
    );

    const onDownloadIconClick = useCallback(() => {
        if (onDownloadClick)
            onDownloadClick(voicemail.message_uid);
    }, [voicemail]);

    useEffect(() => {
        if(isOpen && voicemail) {
            dispatch(actions.getVoicemailDetails.request({
                ...(i_account ? { i_account } : {}),
                message_uid: voicemail.message_uid,
                callback: (details: VoicemailDetails) => {
                    if(isFax) {
                        const objsFile = details?.body_structures?.filter(e => e.file_name?.length);
                        if(objsFile.length > 0) {
                            dispatch(
                                actions.getVoicemailFile.request({
                                    file_name: objsFile[0].file_name,
                                    i_account: i_account,
                                    message_uid: voicemail.message_uid,
                                    callback: (url) => {
                                        setFaxPreview({
                                            url: url,
                                            fileName: objsFile[0].file_name
                                        });
                                    },
                                }),
                            );
                        }
                    }
                }
            }));
        } else {
            setFaxPreview({
                url: '',
                fileName: '',
            });
        }
    }, [voicemail, isOpen, i_account, isFax]);

    const from = useMemo(() => {
        const match2 = voicemailDetails?.from?.match(/^(\d+)\s*</);
        if(match2) {
            return [match2[1], ""];
        }

        const match1 = voicemailDetails?.from?.match(/^(\d+)\s*\(([^)]+)\)/);
        if(match1) {
            return [match1[1], match1[2]];
        }

        const match = voicemailDetails?.from?.match(/#([^\s<]+)\s*</);
        if(match) {
            return [match[1], ""];
        }
        return ["", ""];
    }, [voicemailDetails, extensionList]);

    const deliveryDate = useMemo(() =>  voicemailDetails?.delivery_date
        ? convertUtcToUserLocalTime(
            voicemailDetails.delivery_date,
            timezoneOffset,
            userDateTimeFormat,
        )
        : '', [voicemailDetails]);

    const duration = useMemo(() => {
        const d = dayjs.duration(voicemailDetails?.voicemail_duration ?? 0, 'seconds');
        return d.format('HH:mm:ss');
    }, [voicemailDetails]);

    const size = useMemo(() => {
        return (voicemailDetails?.size ?? 0) + " Kb";
    }, [voicemailDetails]);

    const faxPages = useMemo(() => {
        return voicemailDetails?.fax_pages ?? 0;
    }, [voicemailDetails]);

    const hasRecent = useMemo(() => voicemailDetails?.flags?.includes('\\Recent'), [voicemailDetails]);
    const hasAnswered = useMemo(() => voicemailDetails?.flags?.includes('\\Answered'), [voicemailDetails]);
    const hasSeen = useMemo(() => voicemailDetails?.flags?.includes('\\Seen'), [voicemailDetails]);
    const starred = useMemo(() => voicemailDetails?.flags?.includes('\\Flagged'), [voicemailDetails]);

    const isLoading = useMemo(() => {
        return isLoadingVoicemailDetails ?? false;
    }, [isLoadingVoicemailDetails]);

    const copyContent = useCallback(() => {
        const segments = transcription?.segments ?? [];
        const segment = segments.length ? segments[0] : undefined;
        return segment?.segment?.text ?? '';
    }, [transcription]);

    const playingFiles: PlayingFile[][] = useMemo(() => {
        return [[{
                call_recording_id: (voicemailDetails?.message_uid ?? 0) + '',
                i_xdr: voicemailDetails?.message_uid ?? 0,
                h323_conf_id: 'voicemail',
                customDownloadFileAction: (callback, restrictedCallback) => {
                    customDownloadFileAction?.(voicemailDetails?.message_uid ?? 0, callback, restrictedCallback);
                }
            }]];
    }, [voicemailDetails]);

    return (
        <DialogContainer
            isOpen={isOpen}
            dataQa="voicemail-details-dialog"
            header={!isOpen
                ? ''
                : isFax
                    ? t('screens:voicemails.faxDetails')
                    : t('screens:voicemails.voicemailDetails')}
            customHeaderWidget={(isLoading || !isOpen) ? (<> </>) : (
                <>
                    <div className={classes.statusContainer}>
                        {hasRecent && (<div className={classNames(classes.badge, classes.activeBadge)}>
                            {t('screens:voicemails.new')}
                        </div>)}
                        {hasSeen && (<div className={classNames(classes.badge, classes.inactiveBadge)}>
                            {t('screens:voicemails.seen')}
                        </div>)}
                        {hasAnswered && (<div className={classNames(classes.badge, classes.answered)}>
                            {t('screens:voicemails.answered')}
                        </div>)}
                    </div>
                    <CallerInfo
                        caller={from[0] + " " + from[1]}
                        className={classes.noOverflow}
                        headerClassName={classes.noBold}
                    />
                    <DirectionRight/>
                    <CallerInfo
                        headerClassName={classes.noBold}
                        className={classNames(classes.noOverflow, classes.marginRight)}
                        caller={(extensionId ?? '') + (extensionName ? " (" + extensionName + ")" : '')}
                    />
                </>
            )}
            headerClass={classes.header}
            className={classes.modalContainer}
            dialogActionsButtons={[
                <DialogButton
                    className={classes.primaryActionButton}
                    key="close"
                    label={t('common:close')}
                    onClick={toggleVisibility}
                />,
            ]}
            dialogLeftSideActionsButtons={isLoading ? [] : [
                <DialogButton
                    className={classes.secondaryButton}
                    key="starred"
                    label={starred ? t('screens:voicemails.unstar') : t('screens:voicemails.star')}
                    onClick={() => {onClickStarred?.(voicemail.message_uid, !starred);}}
                    disabled={isReadOnly}
                />,
            ]}
            customHeaderContent={<div className={classes.starContainer}>
                <CustomizedIconButton
                    onClick={() => {
                        onClickStarred?.(voicemail.message_uid, !starred);
                    }}
                    dataTestId={'flagged-icon'}
                >
                    <StarRate
                        className={starred ? classes.starred : classes.notStarred}
                    />
                </CustomizedIconButton>
            </div>}
            customHeaderTitleClass={classes.customHeaderTitleClass}
        >
            {isLoading ? (
                <Loader
                    dataQa="fetch-data-new-menu-loader"
                    absolutePosition={true}
                    dataTestId={'fetch-data-new-menu-loader'}
                />
            ) :
            isOpen ? (
                <Grid className={classes.inputsContainer}>
                    <Grid className={classes.inputRow}>
                        <CustomizedTextField
                            id="deliveryDate"
                            label={t('screens:voicemails.deliveryTime')}
                            value={deliveryDate}
                            readOnly
                            className={classes.textfield}
                        />
                        <CustomizedTextField
                            id="duration"
                            label={isFax ? t('screens:voicemails.pages')
                                : t('screens:voicemails.duration')}
                            value={isFax ? faxPages : duration}
                            readOnly
                            className={classes.textfield}
                        />
                        <CustomizedTextField
                            id="size"
                            label={t('screens:voicemails.size')}
                            value={size}
                            readOnly
                            className={classes.textfield}
                        />
                    </Grid>
                    
                    <Grid className={classNames(classes.recordingActionContainer)}>
                        {isFax ? (
                            <div className={classes.faxDetailsContainer}>
                                <div className={classes.faxTopContainer}>
                                    <div className={classes.faxDetailsHeader}>
                                        {t('screens:voicemails.faxPreview')}
                                    </div>
                                    <div className={classes.faxActionsContainer}>
                                        <CustomizedIconButton
                                            tooltipText={t(
                                                'tooltips:voicemails.downloadFax',
                                            )}
                                            dataQa="details-modal-download-icon"
                                            dataTestId="details-modal-download-icon"
                                            onClick={onDownloadIconClick}
                                        >
                                            <CloudDownload/>
                                        </CustomizedIconButton>

                                        <CustomizedIconButton
                                            tooltipText={t('common:delete')}
                                            dataQa="details-modal-delete"
                                            dataTestId="details-modal-delete"
                                            onClick={onDeleteClick}
                                            className={classes.iconMargin}
                                        >
                                            <Delete/>
                                        </CustomizedIconButton>
                                    </div>
                                </div>
                                <FaxPreview {...faxPreview} />
                            </div>
                        ): (
                            <InternalAudioPlayer
                                dataQa={'audio-player-element'}
                                dataTestId={'audio-player-element'}
                                visible={true}
                                source={playingFiles}
                                customDownloadFileAction={customDownloadFileAction}
                                customClasses={{
                                    rootContainer: classes.playerRootContainer,
                                    extendTextTranscription: classes.extendTextTranscription
                                }}
                                customTranscriptionHeader={<div className={classes.customTranscriptionHeaderContainer}>
                                    <div className={classes.customTranscriptionHeaderText}>{t(t('screens:voicemails.voicemailTranscription'))}</div>
                                    <div>
                                        <CopyContentIcon
                                            copyContent={copyContent()}
                                            tooltipMsg={t('tooltips:voicemails.copyVoicemailTranscription')}
                                        />
                                    </div>
                                </div>}
                                customPlayText={t('tooltips:voicemails.playVoicemail')}
                                customStopText={t('tooltips:voicemails.pauseVoicemail')}
                            >
                                <div className={classes.buttonsContainer}>
                                    <CustomizedIconButton
                                        tooltipText={t(
                                            'tooltips:voicemails.downloadVoicemail',
                                        )}
                                        dataQa="details-modal-download-icon"
                                        dataTestId="details-modal-download-icon"
                                        onClick={onDownloadIconClick}
                                    >
                                        <CloudDownload/>
                                    </CustomizedIconButton>

                                    <CustomizedIconButton
                                        tooltipText={t('common:delete')}
                                        dataQa="details-modal-delete"
                                        dataTestId="details-modal-delete"
                                        onClick={onDeleteClick}
                                        className={classes.iconMargin}
                                    >
                                        <Delete/>
                                    </CustomizedIconButton>
                                </div>
                            </InternalAudioPlayer>
                        )}
                    </Grid>
                </Grid>
            ) : (<></>)}
        </DialogContainer>
    );
};

export default VoicemailDetailsDialog;
